import clsx from "clsx";
import { useRef, type ReactNode } from "react";
import { useAniNavTabs } from "@/components/ui/ani-nav";
import {
  STATUS_DANGER_SURFACE,
  STATUS_WARNING_SURFACE,
} from "@/components/ui/surface/surfaceTokens";

/**
 * Underline-style sub-tabs (secondary navigation): active label + indigo bar,
 * optional count badges. Used on Mission Control bucket filters, mission /
 * client detail headers, and anywhere else you need compact tab rows.
 */
export interface SegmentedTab {
  id: string;
  label: string;
  /** Optional leading icon (e.g. table vs chart). */
  icon?: ReactNode;
  /** When true, only the icon is shown (label used for aria-label / title). */
  iconOnly?: boolean;
  /** When true, the tab is visible but not selectable. */
  disabled?: boolean;
  /** Shown as a trailing pill when set (omit or pass 0 to hide unless `showCountAlways`). */
  count?: number;
  /** Override the rendered count text (e.g. "3 missing"). Falls back to `count`. */
  countLabel?: string;
  /** Show the count pill even when count is 0. */
  showCountAlways?: boolean;
  /** Badge emphasis when count is shown. */
  countVariant?: "neutral" | "attention" | "warning";
}

export interface SegmentedTabsProps {
  tabs: SegmentedTab[];
  activeId: string;
  onChange: (id: string) => void;
  /** Accessible name for the tablist (required when tabs are primary navigation). */
  ariaLabel?: string;
  /** When set, each tab gets `id` and `aria-controls` for paired tabpanels. */
  panelIdPrefix?: string;
  /** Prefix for tab element ids (defaults to `tabTestIdPrefix`). */
  tabIdPrefix?: string;
  /** Merged onto the tablist root (e.g. `mt-6 overflow-x-auto`). */
  className?: string;
  /**
   * When true (default), draws a full-width bottom border on this row.
   * Set false when a parent already owns the separator (e.g. Mission Control toolbar).
   */
  showDivider?: boolean;
  /** Each tab button gets `data-testid="${tabTestIdPrefix}-${tab.id}"`. */
  tabTestIdPrefix?: string;
  /** Register enabled tabs as Floating Ani nav targets (`fe_show` paths). */
  aniNav?: boolean;
}

export function SegmentedTabs({
  tabs,
  activeId,
  onChange,
  ariaLabel,
  panelIdPrefix,
  tabIdPrefix,
  className,
  showDivider = true,
  tabTestIdPrefix = "segmented-tab",
  aniNav = false,
}: SegmentedTabsProps) {
  const idPrefix = tabIdPrefix ?? tabTestIdPrefix;
  const tabElementsRef = useRef(new Map<string, HTMLButtonElement>());
  useAniNavTabs({
    enabled: aniNav,
    tabs: tabs.filter((tab) => !tab.disabled),
    activeTabId: activeId,
    onActivate: onChange,
    getElement: (tabId) => tabElementsRef.current.get(tabId) ?? null,
  });
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={clsx(
        "flex items-center gap-1",
        showDivider && "border-b border-slate-200 dark:border-slate-800/80",
        className
      )}
      data-testid="segmented-tabs"
    >
      {tabs.map((tab) => {
        const active = tab.id === activeId;
        const disabled = tab.disabled ?? false;
        const iconOnly = tab.iconOnly ?? false;
        const tabButtonId = `${idPrefix}-${tab.id}`;
        const panelId = panelIdPrefix ? `${panelIdPrefix}-${tab.id}` : undefined;
        const showCount =
          tab.count !== undefined &&
          (tab.count > 0 || tab.showCountAlways) &&
          (tab.countLabel ?? tab.count) !== undefined;
        const countDisplay = showCount ? String(tab.countLabel ?? tab.count) : "";
        const compactCount = countDisplay.length === 1;
        return (
          <button
            key={tab.id}
            ref={(element) => {
              if (element) tabElementsRef.current.set(tab.id, element);
              else tabElementsRef.current.delete(tab.id);
            }}
            id={tabButtonId}
            role="tab"
            type="button"
            aria-selected={active}
            aria-controls={panelId}
            tabIndex={active ? 0 : -1}
            aria-label={iconOnly ? tab.label : undefined}
            title={iconOnly ? tab.label : undefined}
            disabled={disabled}
            onClick={() => {
              if (!disabled) onChange(tab.id);
            }}
            data-testid={`${tabTestIdPrefix}-${tab.id}`}
            className={clsx(
              "relative py-2 text-sm whitespace-nowrap transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400/40 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-900",
              iconOnly ? "px-2.5" : "px-3",
              disabled
                ? "cursor-not-allowed text-slate-400 dark:text-slate-600"
                : "cursor-pointer",
              !disabled &&
                (active
                  ? "font-semibold text-slate-900 dark:text-white"
                  : "text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"),
              disabled && active && "text-slate-500 dark:text-slate-400",
            )}
          >
            <span className={clsx("inline-flex items-center", !iconOnly && "gap-1.5")}>
              {tab.icon ? (
                <span className={clsx(!iconOnly && "text-[11px] opacity-70")}>{tab.icon}</span>
              ) : null}
              {!iconOnly ? <span>{tab.label}</span> : null}
              {showCount ? (
                <span
                  className={clsx(
                    "inline-flex shrink-0 items-center justify-center rounded-full text-[10px] font-semibold leading-none tabular-nums",
                    compactCount ? "size-[18px] p-0" : "min-h-[18px] min-w-[18px] px-1.5",
                    tab.countVariant === "attention" && tab.count > 0
                      ? STATUS_DANGER_SURFACE
                      : tab.countVariant === "warning" && tab.count > 0
                        ? STATUS_WARNING_SURFACE
                        : active
                          ? "bg-indigo-100 text-indigo-800 dark:bg-indigo-500/25 dark:text-indigo-100"
                          : "bg-slate-200 text-slate-700 dark:bg-slate-700/60 dark:text-slate-300",
                  )}
                >
                  {countDisplay}
                </span>
              ) : null}
            </span>
            {active && (
              <span className="absolute left-2 right-2 -bottom-px h-0.5 rounded-full bg-indigo-600 dark:bg-indigo-300" />
            )}
          </button>
        );
      })}
    </div>
  );
}
